import React from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Globe, LogOut, Award, Flame, Dumbbell, Star } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { usePrefs } from "@/lib/prefs";
import { Avatar, Button, StatTile, Loading } from "@/components/brand";
import { TrainerCard } from "@/components/cards";
import { CatIcon } from "@/components/icons";

export default function Profile() {
  const { user, logout } = useAuth();
  const { language, currency } = usePrefs();
  const stats = useQuery({ queryKey: ["me", "stats"], queryFn: () => api("/me/stats"), enabled: user?.role === "member" });
  const badges = useQuery({ queryKey: ["me", "badges"], queryFn: () => api("/me/badges"), enabled: user?.role === "member" });

  const doLogout = async () => { await logout(); window.location.assign("/"); };

  return (
    <div data-testid="profile-screen" className="max-w-2xl space-y-6">
      <div className="flex items-center gap-4">
        <Avatar uri={user?.avatar} size={72} name={user?.name} />
        <div>
          <h1 className="font-display text-3xl text-ink tracking-tight">{user?.name}</h1>
          <div className="font-body text-mutedgrey">{user?.email}</div>
        </div>
      </div>

      {user?.role === "member" && stats.data ? (
        <div className="flex flex-wrap gap-3">
          <StatTile testID="stat-streak" label="Day streak" value={stats.data.streak} />
          <StatTile testID="stat-points" label="Points" value={badges.data?.points ?? 0} />
          <StatTile testID="stat-workouts" label="Total workouts" value={stats.data.total_workouts} />
          <StatTile testID="stat-week" label="This week" value={`${stats.data.week_workouts} · ${stats.data.week_minutes}m`} />
        </div>
      ) : null}

      {badges.data?.badges?.length ? (
        <div>
          <h3 className="font-display text-xl text-ink mb-2">Badges</h3>
          <div className="flex gap-3 flex-wrap">
            {badges.data.badges.map((b) => (
              <div key={b.challenge_id} className="flex flex-col items-center gap-1 w-24">
                <div className="w-14 h-14 rounded-full bg-brand-soft flex items-center justify-center"><CatIcon name={b.badge_icon} className="text-brand" size={24} /></div>
                <span className="font-body text-[11px] text-ink text-center leading-tight">{b.title}</span>
              </div>
            ))}
          </div>
        </div>
      ) : null}

      {stats.data?.trainers?.length ? (
        <div>
          <h3 className="font-display text-xl text-ink mb-2">Your Trainers</h3>
          <div className="grid gap-3">{stats.data.trainers.map((t) => <TrainerCard key={t.user_id} item={t} />)}</div>
        </div>
      ) : null}

      <div className="space-y-2">
        <Link to="/preferences" data-testid="profile-preferences" className="flex items-center gap-3 p-4 rounded-xl2 border border-divider hover:border-brand transition-colors">
          <Globe size={20} className="text-brand" /><span className="font-body font-semibold text-ink flex-1">Language & Currency</span><span className="font-body text-sm text-mutedgrey">{language.toUpperCase()} · {currency}</span>
        </Link>
        <button onClick={doLogout} data-testid="profile-logout" className="w-full flex items-center gap-3 p-4 rounded-xl2 border border-divider hover:border-destructive transition-colors">
          <LogOut size={20} className="text-destructive" /><span className="font-body font-semibold text-destructive">Log Out</span>
        </button>
      </div>
    </div>
  );
}
